import { useState } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Dialog, DialogContent, DialogTrigger } from "@tenant/ui";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import DOMPurify from "dompurify";
import styles from "./NoticeBanner.module.scss";

export enum NoticePriority {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

const iconMap = {
  [NoticePriority.LOW]: IconNameEnum.ui__infoPopover,
  [NoticePriority.MEDIUM]: IconNameEnum.alertOutline,
  [NoticePriority.HIGH]: IconNameEnum.error,
};

export type NoticeBannerProps = {
  priority?: NoticePriority;
  popupClassName?: string;
  bannerClassName?: string;
  title: string;
  description: string;
  ctaText?: string;
};

export const NoticeBanner = (props: NoticeBannerProps) => {
  const {
    priority = NoticePriority.MEDIUM,
    popupClassName,
    bannerClassName,
    title,
    description,
    ctaText,
  } = props;

  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "NoticeBanner");

  const [isOpen, setIsOpen] = useState(true);

  return (
    isOpen && (
      <div className={clsx(block, bannerClassName, cn("banner", [priority]))}>
        <div className={cn("banner-content")}>
          <Icon
            className={cn("icon", [priority])}
            iconName={iconMap[priority]}
          />
          <span className={cn("title", [priority])}>{title}</span>

          {description?.trim() && (
            <Dialog>
              <DialogTrigger className={cn("cta", [priority])}>
                <span>{ctaText ?? t("apac.noticeBanner.buttonCaption")}</span>
              </DialogTrigger>
              <DialogContent
                title={title}
                className={clsx(
                  block,
                  popupClassName,
                  cn("content", [priority])
                )}
              >
                <div
                  className={cn("description")}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description, {
                      USE_PROFILES: { html: true },
                      ADD_ATTR: ["target"],
                    }),
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
        <button
          className={cn("close-button")}
          onClick={() => setIsOpen(false)}
          aria-label={t("close")}
        >
          <Icon iconName={IconNameEnum.close} />
        </button>
      </div>
    )
  );
};
